import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, CartesianGrid, Legend,
} from 'recharts';
import { ArrowLeft, TrendingUp, DollarSign, Target, Users, Clock, Award, CheckCircle, AlertCircle, FileText, Calendar } from 'lucide-react';

// ── Filtro de meses estilo slicer ────────────────────────────────────────────
function MesFiltro({ meses, selected, onChange }) {
  const porAno = {};
  meses.forEach(m => {
    if (!porAno[m.year]) porAno[m.year] = [];
    porAno[m.year].push(m);
  });
  const anos = Object.keys(porAno).map(Number).sort();
  const isTodos = selected.length === 0;

  const toggleMonth = ym => {
    if (selected.includes(ym)) onChange(selected.filter(m => m !== ym));
    else onChange([...selected, ym]);
  };
  const toggleYear = year => {
    const yearMs = porAno[year].map(m => m.ym);
    const allSel = yearMs.every(m => selected.includes(m));
    if (allSel) onChange(selected.filter(m => !yearMs.includes(m)));
    else onChange([...new Set([...selected, ...yearMs])]);
  };

  const chip = (active, color = '#f6c90e') => ({
    padding: '4px 10px', borderRadius: 5, fontSize: 12, cursor: 'pointer', border: 'none',
    fontWeight: active ? 700 : 400,
    background: active ? color : 'rgba(255,255,255,0.04)',
    color: active ? (color === '#f6c90e' ? '#080c14' : '#e2e8f0') : '#64748b',
    outline: active ? `2px solid ${color}` : 'none',
    outlineOffset: -1,
  });

  return (
    <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:10, padding:'12px 16px', marginBottom:20 }}>
      <div style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:6 }}>
        <button onClick={() => onChange([])} style={chip(isTodos)}>Todos o período</button>
        {anos.length > 0 && <div style={{ width:1, height:22, background:'#1a2035', margin:'0 4px' }} />}
        {anos.map(year => {
          const yearMs = porAno[year].map(m => m.ym);
          const allSel = yearMs.every(m => selected.includes(m));
          const someSel = !allSel && yearMs.some(m => selected.includes(m));
          return (
            <div key={year} style={{ display:'flex', alignItems:'center', gap:4, flexWrap:'wrap' }}>
              <button onClick={() => toggleYear(year)} style={{ ...chip(allSel || someSel, '#60a5fa'), fontWeight:700 }}>{year}</button>
              {porAno[year].map(m => (
                <button key={m.ym} onClick={() => toggleMonth(m.ym)} style={chip(selected.includes(m.ym))}>
                  {m.label}
                </button>
              ))}
              <div style={{ width:1, height:22, background:'#1a2035', margin:'0 2px' }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

const TOOLTIP_STYLE = {
  contentStyle: { background:'#0e1420', border:'1px solid #1a2035', borderRadius:8 },
  labelStyle:   { color:'#e2e8f0', fontWeight:600 },
  itemStyle:    { color:'#e2e8f0' },
};

const S = {
  heading:   { fontSize:22, fontWeight:700, color:'#f6c90e', marginBottom:4 },
  sub:       { fontSize:13, color:'#475569', marginBottom:24 },
  backBtn:   { display:'flex', alignItems:'center', gap:6, background:'none', border:'1px solid #1a2035', borderRadius:8, padding:'7px 14px', color:'#64748b', fontSize:13, cursor:'pointer', marginBottom:20 },
  grid4:     { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:14, marginBottom:20 },
  grid2:     { display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 },
  card:      { background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, padding:18 },
  cardTitle: { fontSize:11, color:'#475569', textTransform:'uppercase', letterSpacing:1, marginBottom:10 },
  metricVal: (c='#f6c90e') => ({ fontSize:26, fontWeight:800, color:c, marginBottom:2 }),
  metricLbl: { fontSize:11, color:'#475569' },
  th:        { padding:'10px 14px', textAlign:'left', fontSize:11, color:'#475569', textTransform:'uppercase', letterSpacing:1, borderBottom:'1px solid #1a2035' },
  td:        { padding:'11px 14px', fontSize:13, color:'#cbd5e1', borderBottom:'1px solid #0f1117' },
  badge:     (c) => ({ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, background:`${c}22`, color:c, border:`1px solid ${c}44` }),
  pipeItem:  { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #0f1117' },
};

const STATUS_LABEL = {
  'novo':'Novo', 'em_contato':'Em Contato', 'proposta_enviada':'Proposta Enviada',
  'aguardando':'Aguardando', 'fechado':'Fechado', 'perdido':'Perdido', 'nao_responde':'Não Responde',
};
const STATUS_COR = {
  'novo':'#60a5fa', 'em_contato':'#f6c90e', 'proposta_enviada':'#c084fc',
  'aguardando':'#fb923c', 'fechado':'#4ade80', 'perdido':'#f87171', 'nao_responde':'#475569',
};

function MetricCard({ icon: Icon, label, value, sub, color = '#f6c90e', iconColor }) {
  return (
    <div style={S.card}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
        <Icon size={16} color={iconColor || color} />
        <span style={S.cardTitle}>{label}</span>
      </div>
      <div style={S.metricVal(color)}>{value}</div>
      {sub && <div style={S.metricLbl}>{sub}</div>}
    </div>
  );
}

export default function VendedorPerformance() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isChefe, isGerenteComercial } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [modalRelatorio, setModalRelatorio] = useState(false);
  const [relPeriodo, setRelPeriodo] = useState({ inicio: '', fim: '' });
  const [gerandoRel, setGerandoRel] = useState(false);

  // Filtro de meses
  const [mesesDisp, setMesesDisp] = useState([]);
  const [filtroMeses, setFiltroMeses] = useState([]);
  const [mostrarFiltro, setMostrarFiltro] = useState(false);

  // Carrega meses disponíveis uma vez por vendedor e seleciona o ano atual por padrão
  useEffect(() => {
    setMesesDisp([]);
    setFiltroMeses([]);
    const anoAtual = new Date().getFullYear();
    api.get(`/vendedor/${id}/performance/meses/`).then(r => {
      const meses = r.data;
      setMesesDisp(meses);
      // Seleciona automaticamente todos os meses do ano atual
      const doAnoAtual = meses.filter(m => m.year === anoAtual).map(m => m.ym);
      if (doAnoAtual.length > 0) setFiltroMeses(doAnoAtual);
    }).catch(() => {});
  }, [id]);

  // Recarrega dados quando id ou filtro muda
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr('');
    const params = filtroMeses.length > 0 ? `?meses=${filtroMeses.join(',')}` : '';
    api.get(`/vendedor/${id}/performance/${params}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setErr(e.response?.data?.error || 'Erro ao carregar dados.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, filtroMeses.join(',')]); // eslint-disable-line

  const labelFiltro = filtroMeses.length === 0
    ? 'Filtrar por mês'
    : `${filtroMeses.length} ${filtroMeses.length === 1 ? 'mês selecionado' : 'meses selecionados'}`;

  // Dados extraídos de data (null-safe)
  const vendedor = data?.vendedor;
  const m = data?.metricas;
  const grafico_mensal = data?.grafico_mensal ?? [];
  const pipeline = data?.pipeline ?? {};
  const clientes = data?.clientes ?? [];

  // Format month labels (2024-03 → Mar/24)
  const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  const grafico = grafico_mensal.map(g => {
    const [ano, mes] = g.mes.split('-');
    return { ...g, label: `${MESES[parseInt(mes)-1]}/${ano.slice(2)}` };
  });

  const pipelineEntries = Object.entries(pipeline).sort((a,b) => b[1]-a[1]);

  const bR = (n) => n !== null && n !== undefined
    ? `R$ ${Number(n).toLocaleString('pt-BR', { minimumFractionDigits:2 })}`
    : '—';

  const gerarRelatorio = async () => {
    if (!relPeriodo.inicio || !relPeriodo.fim) return;
    setGerandoRel(true);
    try {
      const resp = await api.get(
        `/vendedor/${id}/relatorio/?data_inicio=${relPeriodo.inicio}&data_fim=${relPeriodo.fim}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_${(vendedor?.nome || 'vendedor').replace(/\s+/g,'_')}_${relPeriodo.inicio}_${relPeriodo.fim}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setModalRelatorio(false);
    } catch (e) {
      alert('Erro ao gerar relatório. Verifique o período e tente novamente.');
    } finally {
      setGerandoRel(false);
    }
  };

  const gerarHistorico = async () => {
    setGerandoPdf(true);
    try {
      const params = (isChefe() || isGerenteComercial()) ? `?vendedor_id=${id}` : '';
      const resp = await api.get(`/financeiro/historico-comissoes/${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `comissoes_${(vendedor?.nome || 'vendedor').replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Erro ao gerar histórico de comissões.');
    } finally {
      setGerandoPdf(false);
    }
  };

  return (
    <>
    <div>
      {/* Barra superior — sempre visível */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <button style={S.backBtn} onClick={() => navigate(-1)}>
          <ArrowLeft size={15} /> Voltar
        </button>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
          {/* Botão filtrar por mês */}
          <button
            onClick={() => setMostrarFiltro(f => !f)}
            style={{
              display:'flex', alignItems:'center', gap:7,
              padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer',
              background: filtroMeses.length > 0 ? 'rgba(246,201,14,0.12)' : 'transparent',
              color: filtroMeses.length > 0 ? '#f6c90e' : '#64748b',
              border: `1px solid ${filtroMeses.length > 0 ? 'rgba(246,201,14,0.4)' : '#1a2035'}`,
            }}
          >
            <Calendar size={15} />
            {labelFiltro}
          </button>
          <button
            onClick={() => setModalRelatorio(true)}
            style={{
              display:'flex', alignItems:'center', gap:7,
              padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer',
              background:'rgba(96,165,250,0.12)', color:'#60a5fa',
              border:'1px solid rgba(96,165,250,0.3)',
            }}
          >
            <FileText size={15} />
            Gerar Relatório
          </button>
          <button
            onClick={gerarHistorico}
            disabled={gerandoPdf}
            style={{
              display:'flex', alignItems:'center', gap:7,
              padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:600, cursor: gerandoPdf ? 'wait' : 'pointer',
              background:'rgba(246,201,14,0.12)', color:'#f6c90e',
              border:'1px solid rgba(246,201,14,0.3)',
              opacity: gerandoPdf ? 0.6 : 1,
            }}
          >
            <FileText size={15} />
            {gerandoPdf ? 'Gerando...' : 'Histórico de Comissões'}
          </button>
        </div>
      </div>

      {/* Slicer de meses */}
      {mostrarFiltro && mesesDisp.length > 0 && (
        <MesFiltro meses={mesesDisp} selected={filtroMeses} onChange={setFiltroMeses} />
      )}

      {/* Heading — usa data quando disponível */}
      {vendedor && (
        <>
          <div style={S.heading}>{vendedor.nome}</div>
          <div style={S.sub}>
            {vendedor.cargo || 'Vendedor'}
            {vendedor.percentual_comissao > 0 && ` · Comissão atual: ${vendedor.percentual_comissao}%`}
          </div>
        </>
      )}

      {/* Estados de carregamento / erro */}
      {loading && <div style={{ color:'#475569', padding:'20px 0' }}>Carregando...</div>}
      {err && !loading && <div style={{ color:'#f87171', padding:'20px 0' }}>{err}</div>}

      {/* Métricas + conteúdo — só quando data está carregada */}
      {data && (
      <>
      {/* Metrics */}
      <div style={S.grid4}>
        <MetricCard icon={DollarSign} label="Receita Total" value={bR(m.receita_total)} sub={`${m.total_fechados} venda(s) fechada(s)`} color="#f6c90e" />
        <MetricCard icon={TrendingUp} label="Taxa Conversão" value={`${m.taxa_conversao}%`} sub={`${m.total_fechados} / ${m.total_leads} leads`} color="#4ade80" />
        <MetricCard icon={Target}     label="Ticket Médio"  value={bR(m.ticket_medio)} sub="Por venda fechada" color="#60a5fa" />
        <MetricCard icon={Clock}      label="Tempo Médio"   value={m.tempo_medio_dias != null ? `${m.tempo_medio_dias} dias` : '—'} sub="Do lead ao fechamento" color="#c084fc" />
      </div>

      {/* Comissão */}
      <div style={{ ...S.grid4, gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))' }}>
        <MetricCard icon={Award} label="Comissão Total" value={bR(m.comissao_total)} sub="Calculada sobre fechados" color="#f6c90e" />
        <MetricCard icon={CheckCircle} label="Comissão Paga" value={bR(m.comissao_paga)} sub="Já recebida" color="#4ade80" iconColor="#4ade80" />
        <div style={{ position:'relative' }}>
          <MetricCard icon={AlertCircle} label="Comissão Pendente" value={bR(m.comissao_pendente)} sub="Aguardando pagamento" color="#fb923c" iconColor="#fb923c" />
          {m.comissao_pendente_com_desconto && (
            <div title="Há vendas com mais de 5% de desconto — comissão aguarda revisão do chefe"
              style={{ position:'absolute', top:8, right:8, background:'#f59e0b', borderRadius:'50%', width:20, height:20, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800, color:'#fff', cursor:'default' }}>⚠</div>
          )}
        </div>
        <MetricCard icon={Users} label="Total de Leads" value={m.total_leads} sub="Leads atribuídos" color="#64748b" />
      </div>

      {/* Meta do mês */}
      {m.meta_mensal && (
        <div style={{ ...S.card, marginBottom:20 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
            <div style={{ fontSize:14, fontWeight:600, color:'#e2e8f0' }}>Meta do Mês</div>
            <div style={{ fontSize:13, color:'#f6c90e', fontWeight:700 }}>
              {bR(m.vendas_mes_atual)} / {bR(m.meta_mensal)}
            </div>
          </div>
          <div style={{ height:10, background:'#1a2035', borderRadius:5, overflow:'hidden' }}>
            <div style={{
              width:`${Math.min(m.meta_pct || 0, 100)}%`,
              height:'100%',
              background: (m.meta_pct || 0) >= 100 ? '#4ade80' : (m.meta_pct || 0) >= 60 ? '#f6c90e' : '#fb923c',
              borderRadius:5,
              transition:'width 0.5s ease',
            }} />
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:6 }}>
            <span style={{ fontSize:12, color:'#475569' }}>Vendas este mês</span>
            <span style={{ fontSize:12, fontWeight:700, color: (m.meta_pct||0) >= 100 ? '#4ade80' : (m.meta_pct||0) >= 60 ? '#f6c90e' : '#fb923c' }}>
              {m.meta_pct != null ? `${m.meta_pct}%` : '—'}
            </span>
          </div>
        </div>
      )}

      {/* Line chart + Pipeline */}
      <div style={S.grid2}>
        {/* Monthly chart */}
        <div style={S.card}>
          <div style={S.cardTitle}>Fechamentos por Mês</div>
          {grafico.length === 0 ? (
            <div style={{ color:'#475569', fontSize:13, textAlign:'center', padding:40 }}>Nenhum fechamento registrado</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={grafico} margin={{ top:5, right:30, bottom:5, left:0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a2035" />
                <XAxis dataKey="label" tick={{ fill:'#475569', fontSize:11 }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="receita" orientation="left"  tick={{ fill:'#475569', fontSize:11 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <YAxis yAxisId="qtd"     orientation="right" tick={{ fill:'#475569', fontSize:11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  {...TOOLTIP_STYLE}
                  formatter={(v, name) => [
                    name === 'receita' ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits:2 })}` : v,
                    name === 'receita' ? 'Receita' : 'Qtd vendas',
                  ]}
                />
                <Legend formatter={n => n === 'receita' ? 'Receita (R$)' : 'Qtd vendas'} wrapperStyle={{ fontSize:11, color:'#64748b' }} />
                <Line yAxisId="receita" type="monotone" dataKey="receita"  stroke="#f6c90e" strokeWidth={2} dot={{ fill:'#f6c90e', r:3 }} activeDot={{ r:5 }} />
                <Line yAxisId="qtd"     type="monotone" dataKey="fechados" stroke="#60a5fa" strokeWidth={2} dot={{ fill:'#60a5fa', r:3 }} activeDot={{ r:5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Pipeline */}
        <div style={S.card}>
          <div style={S.cardTitle}>Pipeline Atual</div>
          {pipelineEntries.length === 0 ? (
            <div style={{ color:'#475569', fontSize:13, textAlign:'center', padding:40 }}>Sem leads</div>
          ) : (
            <>
              {pipelineEntries.map(([status, count]) => {
                const cor = STATUS_COR[status] || '#64748b';
                const label = STATUS_LABEL[status] || status;
                const total = pipelineEntries.reduce((s, [,c]) => s + c, 0);
                const pct = Math.round(count / total * 100);
                return (
                  <div key={status} style={S.pipeItem}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, flex:1 }}>
                      <div style={{ width:10, height:10, borderRadius:'50%', background:cor, flexShrink:0 }} />
                      <span style={{ fontSize:13, color:'#cbd5e1' }}>{label}</span>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:60, height:6, background:'#1a2035', borderRadius:3 }}>
                        <div style={{ width:`${pct}%`, height:'100%', background:cor, borderRadius:3 }} />
                      </div>
                      <span style={{ fontSize:13, fontWeight:700, color:cor, minWidth:28, textAlign:'right' }}>{count}</span>
                    </div>
                  </div>
                );
              })}
              <div style={{ marginTop:10, fontSize:11, color:'#475569' }}>
                Total: {pipelineEntries.reduce((s,[,c]) => s+c, 0)} leads
              </div>
            </>
          )}
        </div>
      </div>

      {/* Client table */}
      <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, overflow:'hidden' }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid #1a2035', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontSize:14, fontWeight:600, color:'#e2e8f0' }}>Clientes</div>
          <div style={{ fontSize:12, color:'#475569' }}>{clientes.length} lead(s)</div>
        </div>
        <div style={{ overflowX:'auto', overflowY:'auto', maxHeight:400 }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={S.th}>Cliente</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Valor Venda</th>
                <th style={S.th}>Comissão</th>
                <th style={S.th}>Status Comissão</th>
                <th style={S.th}>Data Venda</th>
              </tr>
            </thead>
            <tbody>
              {clientes.length === 0 && (
                <tr><td colSpan={6} style={{ ...S.td, textAlign:'center', color:'#475569', padding:32 }}>Nenhum lead</td></tr>
              )}
              {clientes.map(c => (
                <tr key={c.lead_id}
                  style={{ cursor:'pointer' }}
                  onClick={() => navigate(`/jornada/${c.cliente_id}`)}
                  onMouseEnter={e => e.currentTarget.style.background='#111827'}
                  onMouseLeave={e => e.currentTarget.style.background=''}>
                  <td style={S.td}><strong style={{ color:'#e2e8f0' }}>{c.cliente_nome}</strong></td>
                  <td style={S.td}>
                    <span style={S.badge(STATUS_COR[c.status] || '#64748b')}>
                      {STATUS_LABEL[c.status] || c.status_display || c.status}
                    </span>
                  </td>
                  <td style={S.td}>
                    {c.valor_total ? (
                      <span style={{ color:'#f6c90e', fontWeight:600 }}>{bR(c.valor_total)}</span>
                    ) : '—'}
                  </td>
                  <td style={S.td}>
                    <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                      {c.comissao ? (
                        <span style={{ color:'#4ade80', fontWeight:600 }}>{bR(c.comissao)}</span>
                      ) : '—'}
                      {c.desconto_revisao_pendente && c.status !== 'fechado' && (
                        <span title="Venda com mais de 5% de desconto — comissão será revisada pelo chefe"
                          style={{ background:'#f59e0b', borderRadius:'50%', width:16, height:16, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, color:'#fff', cursor:'default', flexShrink:0 }}>⚠</span>
                      )}
                    </div>
                  </td>
                  <td style={S.td}>
                    {c.comissao_status === 'paga' ? (
                      <span style={S.badge('#4ade80')}>✓ Paga {c.comissao_paga_em ? `· ${c.comissao_paga_em}` : ''}</span>
                    ) : c.comissao_status === 'pendente' ? (
                      <span style={S.badge('#fb923c')}>Pendente</span>
                    ) : '—'}
                  </td>
                  <td style={S.td}>{c.data_venda || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
    </div>

      {modalRelatorio && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}
          onClick={e => e.target === e.currentTarget && setModalRelatorio(false)}>
          <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, padding:28, width:360, display:'flex', flexDirection:'column', gap:18 }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#e2e8f0' }}>Gerar Relatório de Vendas</div>
            <div style={{ fontSize:12, color:'#64748b' }}>
              Selecione o período. Somente vendas <b style={{ color:'#4ade80' }}>fechadas</b> serão incluídas.
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div>
                <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Data inicial</label>
                <input type="date" value={relPeriodo.inicio}
                  onChange={e => setRelPeriodo(p => ({ ...p, inicio: e.target.value }))}
                  style={{ background:'#080c14', border:'1px solid #1a2035', borderRadius:8, padding:'9px 12px', color:'#e2e8f0', fontSize:13, width:'100%' }} />
              </div>
              <div>
                <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Data final</label>
                <input type="date" value={relPeriodo.fim}
                  onChange={e => setRelPeriodo(p => ({ ...p, fim: e.target.value }))}
                  style={{ background:'#080c14', border:'1px solid #1a2035', borderRadius:8, padding:'9px 12px', color:'#e2e8f0', fontSize:13, width:'100%' }} />
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={() => setModalRelatorio(false)}
                style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'transparent', color:'#64748b', cursor:'pointer', fontSize:13 }}>
                Cancelar
              </button>
              <button onClick={gerarRelatorio}
                disabled={gerandoRel || !relPeriodo.inicio || !relPeriodo.fim}
                style={{
                  padding:'8px 20px', borderRadius:8, border:'none', fontSize:13, fontWeight:600,
                  cursor: gerandoRel ? 'wait' : 'pointer',
                  background:'#60a5fa', color:'#080c14',
                  opacity: (gerandoRel || !relPeriodo.inicio || !relPeriodo.fim) ? 0.5 : 1,
                }}>
                {gerandoRel ? 'Gerando PDF...' : 'Gerar PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
